import { mapRowToInvoiceBrandingForm } from "./invoiceBrandingHelpers";
import { sanitizeClientAddressForDisplay } from "./applicationCostHelpers";
import { formatBranchContactNumber } from "./invoicePreviewHelpers";
import { resolveCreateInvoiceStatusFromPayment } from "./invoiceStatusHelpers";
import { mergeInvoiceDiscountIntoTotals } from "./invoiceDiscountHelpers";
import {
  buildComputedLineItem,
  normalizeTaxType,
  resolveExTaxFooterTotals,
} from "./invoiceLineItemHelpers";
import {
  getInvoiceSettingApiMessage,
  isInvoiceSettingApiSuccess,
} from "./invoiceSettingHelpers";
import { apiUrl } from "../components/ApiManager/AxiosHeaders";
import { mergeBillToPrintOptionsIntoSnapshot } from "../components/reports/commissions/commissionBillToUtils";

const isFinanceInvoiceApiSuccess = isInvoiceSettingApiSuccess;
const getFinanceInvoiceApiMessage = getInvoiceSettingApiMessage;

/** Finance invoice API expects `none` for no tax; the UI uses `notax`. */
export function mapFinanceInvoiceTaxTypeForApi(value) {
  const normalized = normalizeTaxType(value);
  if (!normalized || normalized === "notax") return "none";
  return normalized;
}

export function mapFinanceInvoicePayloadTaxTypesForApi(payload = {}) {
  if (!payload || typeof payload !== "object") return payload;

  const next = { ...payload };
  if ("tax_type" in next) {
    next.tax_type = mapFinanceInvoiceTaxTypeForApi(next.tax_type);
  }
  if ("gst_status" in next) {
    next.gst_status = mapFinanceInvoiceTaxTypeForApi(next.gst_status);
  }
  if (Array.isArray(next.line_items)) {
    next.line_items = next.line_items.map((row) => {
      if (!row || typeof row !== "object") return row;
      const mapped = { ...row };
      if ("tax_type" in mapped) {
        mapped.tax_type = mapFinanceInvoiceTaxTypeForApi(mapped.tax_type);
      }
      if ("gst_status" in mapped) {
        mapped.gst_status = mapFinanceInvoiceTaxTypeForApi(mapped.gst_status);
      }
      if ("gst_type" in mapped) {
        mapped.gst_type = mapFinanceInvoiceTaxTypeForApi(mapped.gst_type);
      }
      return mapped;
    });
  }
  return next;
}

export function buildCreateInvoicePaymentRecord(payment = {}) {
  const record = {
    transaction_type: payment.transaction_type || "payment",
    amount: Number(payment.amount) || 0,
    payment_method: payment.payment_method || "cash",
    transaction_date: payment.transaction_date || "",
    reference: String(payment.reference || "").trim(),
    description: String(payment.description || "").trim() || "Payment received",
  };

  if (payment.finance_invoice_item_id) {
    record.finance_invoice_item_id = Number(payment.finance_invoice_item_id);
  }
  if (payment.commission_installment_id) {
    record.commission_installment_id = Number(payment.commission_installment_id);
  }
  if (payment.bonus_installment_id) {
    record.bonus_installment_id = Number(payment.bonus_installment_id);
  }
  if (payment.line_number) {
    record.line_number = Number(payment.line_number);
  }

  return record;
}

export function sumCreateInvoicePayments(payments = []) {
  return (payments || []).reduce(
    (sum, payment) => sum + Math.max(Number(payment?.amount) || 0, 0),
    0,
  );
}

/**
 * Builds JSON or multipart body for savefinanceinvoice.
 * Uses FormData when a receipt file is attached to the payment.
 */
// export function buildInvoiceCreateRequestBody(payload = {}) {
//   const receiptFile = payload?._receiptFile || null;
//   const cleanPayload = { ...payload };
//   delete cleanPayload._receiptFile;

//   if (!receiptFile) {
//     return { body: cleanPayload, isFormData: false };
//   }

//   const formData = new FormData();

//   Object.entries(cleanPayload).forEach(([key, value]) => {
//     if (value == null) return;

//     if (key === "payment" && typeof value === "object" && !Array.isArray(value)) {
//       Object.entries(value).forEach(([paymentKey, paymentValue]) => {
//         if (paymentValue == null) return;
//         formData.append(
//           `payment[${paymentKey}]`,
//           typeof paymentValue === "object"
//             ? JSON.stringify(paymentValue)
//             : String(paymentValue),
//         );
//       });
//       return;
//     }

//     if (typeof value === "object") {
//       formData.append(key, JSON.stringify(value));
//       return;
//     }

//     formData.append(key, String(value));
//   });

//   formData.append("attachment", receiptFile);

//   return { body: formData, isFormData: true };
// }

export function buildInvoiceCreateRequestBody(payload = {}) {
  const receiptFiles = payload._receiptFiles || [];
  const cleanPayload = { ...payload };
  delete cleanPayload._receiptFiles;

  const hasFiles = Array.isArray(receiptFiles) && receiptFiles.length > 0;

  if (!hasFiles) {
    return { body: cleanPayload, isFormData: false };
  }

  const formData = new FormData();

  // ----- Copy this loop from the old version -----
  Object.entries(cleanPayload).forEach(([key, value]) => {
    if (value == null) return;

    if (
      key === "payment" &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      Object.entries(value).forEach(([paymentKey, paymentValue]) => {
        if (paymentValue == null) return;
        formData.append(
          `payment[${paymentKey}]`,
          typeof paymentValue === "object"
            ? JSON.stringify(paymentValue)
            : String(paymentValue),
        );
      });
      return;
    }

    if (key === "payments" && Array.isArray(value)) {
      formData.append("payments", JSON.stringify(value));
      return;
    }

    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, String(value));
  });

  // Index must match payments[] index (attachment_0 → payments[0], etc.)
  receiptFiles.forEach((paymentFiles, paymentIndex) => {
    const files = Array.isArray(paymentFiles)
      ? paymentFiles.filter(Boolean)
      : paymentFiles
        ? [paymentFiles]
        : [];

    files.forEach((file, fileIndex) => {
      if (!file) return;
      formData.append(`attachment_${paymentIndex}[${fileIndex}]`, file);
      formData.append(`attachment_${paymentIndex}[]`, file);
      if (fileIndex === 0) {
        formData.append(`attachment_${paymentIndex}`, file);
        if (paymentIndex === 0) {
          formData.append("attachment", file);
        }
      } else {
        formData.append(`attachment_${paymentIndex}_${fileIndex}`, file);
      }
    });
  });

  return { body: formData, isFormData: true };
}

export function buildBrandingSnapshot(branding) {
  if (!branding) return null;

  return {
    id: branding.id ?? null,
    ...mapRowToInvoiceBrandingForm(branding),
  };
}

export function buildBranchSnapshot(branch) {
  if (!branch) return null;

  return {
    id: branch.id ?? null,
    agencyname: branch.agencyname || "",
    agentname: branch.agentname || branch.agentName || "",
    name: branch.name || "",
    email: branch.email || "",
    phone: formatBranchContactNumber(branch) || "",
    country_code: branch.country_code || "",
    contact: branch.contact || "",
    address: branch.address || "",
    country: branch.country || "",
  };
}

export function buildClientSnapshot(clientProfile, clientId) {
  if (!clientProfile && !clientId) return null;

  return {
    id: clientProfile?.id || clientId || null,
    full_name: clientProfile?.fullName || "",
    email: clientProfile?.email || "",
    phone: clientProfile?.phone || "",
    address: String(clientProfile?.address || "").trim(),
    country: clientProfile?.country || "",
    city: clientProfile?.city || "",
    street_address: clientProfile?.street_address || "",
    h_address: clientProfile?.h_address || "",
    street: clientProfile?.street || "",
  };
}

export function buildInvoiceLineItemsPayload(lineItems = []) {
  return lineItems.map((row, index) => {
    const computed = buildComputedLineItem(row);
    const lineTaxType = normalizeTaxType(computed.tax_type) || "notax";
    const apiTaxType = mapFinanceInvoiceTaxTypeForApi(lineTaxType);
    const item = {
      line_number: index + 1,
      description: String(row.description || "").trim(),
      quantity: Number(row.quantity) || 0,
      unit_price: Number(row.unit_price) || 0,
      discount: Number(row.discount) || 0,
      discount_type:
        row.discount_tab === "percentage" ||
        row.discount_percent != null ||
        row.discount_percentage != null
          ? "percentage"
          : row.discount_rule_id
            ? "percentage"
            : Number(row.discount) > 0
              ? "fixed"
              : null,
      discount_percentage:
        row.discount_percent != null && String(row.discount_percent).trim() !== ""
          ? Number(row.discount_percent)
          : row.discount_percentage != null &&
              String(row.discount_percentage).trim() !== ""
            ? Number(row.discount_percentage)
            : null,
      discount_reason: String(row.discount_reason || "").trim(),
      discount_rule_id: row.discount_rule_id
        ? Number(row.discount_rule_id)
        : null,
      tax_type: apiTaxType,
      gst_status: apiTaxType,
      gst_type: apiTaxType,
      tax_rate: Number(computed.tax_rate) || 0,
      gst_percentage: Number(computed.tax_rate) || 0,
      net: Number(computed.net) || 0,
      tax_amount: Number(computed.tax_amount) || 0,
      gst_amount: Number(computed.tax_amount) || 0,
      amount: Number(computed.amount) || 0,
      application_id: row.application_id ? Number(row.application_id) : null,
      application_type: row.application_type || "",
      fee_type: row.fee_type || "",
    };

    if (row.commission_installment_id) {
      item.commission_installment_id = Number(row.commission_installment_id);
    }
    if (row.bonus_installment_id) {
      item.bonus_installment_id = Number(row.bonus_installment_id);
    }

    if (row.student_discount != null && row.student_discount !== "") {
      item.student_discount = Number(row.student_discount) || 0;
    }
    if (row.discount_paid != null && row.discount_paid !== "") {
      item.discount_paid = Number(row.discount_paid) || 0;
    }
    if (row.discount_balance != null && row.discount_balance !== "") {
      item.discount_balance = Number(row.discount_balance) || 0;
    }
    if (row.balance_fee != null && row.balance_fee !== "") {
      item.balance_fee = Number(row.balance_fee) || 0;
    }
    if (row.net_commission != null && row.net_commission !== "") {
      item.net_commission = Number(row.net_commission) || 0;
    }
    const isCommissionTuitionLine =
      item.commission_installment_id ||
      String(item.fee_type || "").toLowerCase() === "tuition";
    if (isCommissionTuitionLine) {
      const qty = Number(row.quantity) || 0;
      const unit = Number(row.unit_price) || 0;
      if (qty > 0 && unit > 0) {
        item.expected_commission = Math.round(unit * qty * 100) / 100;
      }
    }

    return item;
  });
}

export function buildInvoiceCreatePayload({
  status,
  invoiceTitle = "",
  invoiceNumber,
  issueDate,
  dueDate,
  reference,
  documentType = "",
  documentTaxType,
  branchId,
  selectedBranch,
  brandingId,
  selectedBranding,
  application,
  applicationType = "",
  clientProfile,
  billToProfile = null,
  clientId,
  currency = "AUD",
  lineTotals,
  invoiceDiscount = null,
  html = "",
  email = "",
  sendEmail = true,
  payment = null,
  payments = null,
  receiptFiles = null,
  externalReferringAgent = null,
  externalProcessingAgent = null,
  commissionBillToPrintOptions = null,
}) {
  const baseLineTotals = lineTotals || {};
  const mergedTotals = invoiceDiscount
    ? mergeInvoiceDiscountIntoTotals(baseLineTotals, invoiceDiscount)
    : baseLineTotals;
  const footerTotals = resolveExTaxFooterTotals(
    baseLineTotals?.lineItems || [],
    documentTaxType,
  );
  const totalAmount =
    Number(footerTotals?.total) > 0
      ? Number(footerTotals.total)
      : Number(mergedTotals?.total_amount) || 0;
  const grossTotal = Number(mergedTotals?.gross_subtotal) || 0;
  const lineDiscountTotal = Number(baseLineTotals?.discount_total) || 0;
  const invoiceDiscountAmount = invoiceDiscount
    ? Number(invoiceDiscount.amount) || 0
    : 0;
  const discountTotal = invoiceDiscountAmount || lineDiscountTotal;

  const paymentList = Array.isArray(payments)
    ? payments.filter((item) => Number(item?.amount) > 0)
    : payment && Number(payment?.amount) > 0
      ? [payment]
      : [];
  const draftPaymentTotal = sumCreateInvoicePayments(paymentList);
  const useIncrementalPayments = paymentList.length > 1;
  const paidAmount = useIncrementalPayments ? 0 : draftPaymentTotal;
  const amountDue = useIncrementalPayments
    ? totalAmount
    : Math.max(totalAmount - draftPaymentTotal, 0);
  const resolvedStatus = resolveCreateInvoiceStatusFromPayment({
    requestedStatus: status,
    totalAmount,
    paidAmount: draftPaymentTotal,
    discountAmount: discountTotal,
    grossSubtotal: grossTotal,
  });

  const payload = {
    status: resolvedStatus,
    invoice_title: invoiceTitle,
    invoice_number: invoiceNumber,
    issue_date: issueDate,
    due_date: dueDate,
    reference: reference || "",
    currency,
    document_type: documentType || "invoice",

    application_id: application?.id ?? null,
    application_type: applicationType,
    client_id: clientProfile?.id || clientId || null,
    branch_id: branchId ? Number(branchId) : null,
    branding_id: brandingId ? Number(brandingId) : null,

    client_snapshot: mergeBillToPrintOptionsIntoSnapshot(
      buildClientSnapshot(billToProfile || clientProfile, clientId),
      commissionBillToPrintOptions,
    ),
    branding_snapshot: buildBrandingSnapshot(selectedBranding),
    branch_snapshot: buildBranchSnapshot(selectedBranch),

    line_items: buildInvoiceLineItemsPayload(baseLineTotals?.lineItems),

    gross_total: grossTotal,
    discount_total: discountTotal,
    subtotal: Number(baseLineTotals?.subtotal) || 0,
    tax_amount: Number(baseLineTotals?.tax_amount) || 0,
    inclusive_tax_amount: Number(baseLineTotals?.inclusive_tax_amount) || 0,
    exclusive_tax_amount: Number(baseLineTotals?.exclusive_tax_amount) || 0,
    total_amount: totalAmount,
    tax_type: mapFinanceInvoiceTaxTypeForApi(documentTaxType),
    gst_status: mapFinanceInvoiceTaxTypeForApi(documentTaxType),

    paid_amount: paidAmount,
    amount_due: amountDue,
    external_referring_agent: externalReferringAgent
      ? Number(externalReferringAgent)
      : null,
    external_processing_agent: externalProcessingAgent
      ? Number(externalProcessingAgent)
      : null,
  };

  if (invoiceDiscount) {
    payload.discount_reason = invoiceDiscount.description;
  }

  if (paymentList.length === 1) {
    payload.payment = buildCreateInvoicePaymentRecord(paymentList[0]);
  } else if (paymentList.length > 1) {
    payload.payments = paymentList.map(buildCreateInvoicePaymentRecord);
  }

  // When saving as awaiting payment / approved (no payment recorded), include email HTML
  // + recipient so the backend can create and email in one request.
  if (
    (resolvedStatus === "awaiting_payment" || resolvedStatus === "approved") &&
    paidAmount <= 0 &&
    sendEmail
  ) {
    payload.html = String(html || "");
    payload.email =
      String(email || "").trim() ||
      String(billToProfile?.email || clientProfile?.email || "").trim();
  }

  const normalizedReceiptFiles = Array.isArray(receiptFiles)
    ? receiptFiles
    : receiptFiles
      ? [receiptFiles]
      : paymentList.length > 0
        ? paymentList.map((item) => {
            if (Array.isArray(item?.receiptFiles) && item.receiptFiles.length) {
              return item.receiptFiles.filter(Boolean);
            }
            return item?.receiptFile ? [item.receiptFile] : [];
          })
        : [];

  if (normalizedReceiptFiles.some((entry) =>
    Array.isArray(entry) ? entry.some(Boolean) : Boolean(entry),
  )) {
    payload._receiptFiles = normalizedReceiptFiles;
  }

  return payload;
}

export {
  isInvoiceSettingApiSuccess as isInvoiceCreateApiSuccess,
  isInvoiceSettingApiSuccess as isFinanceInvoiceApiSuccess,
  getInvoiceSettingApiMessage as getInvoiceCreateApiMessage,
  getInvoiceSettingApiMessage as getFinanceInvoiceApiMessage,
};

export function normalizeFinanceInvoiceList(response) {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload && typeof payload === "object") {
    return [payload];
  }

  return [];
}

function mapFinanceExpectedPaymentFields(raw = {}) {
  return {
    expectedPaymentDate:
      raw.expected_payment_date ?? raw.expectedPaymentDate ?? null,
    expectedPaymentNote:
      raw.expected_payment_note ?? raw.expectedPaymentNote ?? "",
  };
}

/** Apply expected-payment patch; null clears date/note (?? would keep old values). */
export function mergeExpectedPaymentPatch(row = {}, patch = {}) {
  const next = { ...row };

  if (Object.prototype.hasOwnProperty.call(patch, "expectedPaymentDate")) {
    next.expected_payment_date = patch.expectedPaymentDate;
    next.expectedPaymentDate = patch.expectedPaymentDate;
  }

  if (Object.prototype.hasOwnProperty.call(patch, "expectedPaymentNote")) {
    next.expected_payment_note = patch.expectedPaymentNote;
    next.expectedPaymentNote = patch.expectedPaymentNote;
  }

  return next;
}

const FINANCE_INVOICE_LIST_STATUS_TABS = new Set([
  "all",
  "draft",
  "awaiting_approval",
  "awaiting_payment",
  "partial",
  "paid",
  "void",
]);

const FINANCE_INVOICE_LIST_STATUS_SLUGS = {
  all: "all",
  draft: "draft",
  awaiting_payment: "awaiting-payment",
  awaiting_approval: "awaiting-approval",
  partial: "partial",
  paid: "paid",
  void: "void",
};

function normalizeInvoiceListStatusTab(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  return FINANCE_INVOICE_LIST_STATUS_TABS.has(raw) ? raw : null;
}

function normalizeInvoiceListModuleTab(value) {
  return String(value || "").trim().toLowerCase() === "commission"
    ? "commission"
    : "application";
}

function invoiceListStatusTabSlug(statusTab) {
  const normalized = normalizeInvoiceListStatusTab(statusTab) || "draft";
  return FINANCE_INVOICE_LIST_STATUS_SLUGS[normalized] || "draft";
}

function readInvoiceListStatusTabFromParams(params) {
  const named = normalizeInvoiceListStatusTab(
    params.get("tab") || params.get("status") || params.get("statusTab"),
  );
  if (named) return named;

  for (const id of FINANCE_INVOICE_LIST_STATUS_TABS) {
    const slug = FINANCE_INVOICE_LIST_STATUS_SLUGS[id] || id;
    if (params.has(id) || params.has(slug)) return id;
  }

  return "draft";
}

const FINANCE_INVOICES_BASE_PATH = "/finance/invoices";
const FINANCE_INVOICES_COMMISSION_PATH = "/finance/invoices/commission";
const FINANCE_INVOICES_APPLICATION_PATH = "/finance/invoices/application";

function readInvoiceListModuleTabFromParams(params) {
  const raw = params.get("module") || params.get("moduleTab");
  if (!raw) return null;
  return normalizeInvoiceListModuleTab(raw);
}

export function readFinanceInvoiceModuleFromPathname(pathname = "") {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  if (path === FINANCE_INVOICES_COMMISSION_PATH) return "commission";
  if (
    path === FINANCE_INVOICES_APPLICATION_PATH ||
    path === FINANCE_INVOICES_BASE_PATH
  ) {
    return "application";
  }
  return null;
}

export function buildFinanceInvoicesPath(moduleTab = "application") {
  return normalizeInvoiceListModuleTab(moduleTab) === "commission"
    ? FINANCE_INVOICES_COMMISSION_PATH
    : FINANCE_INVOICES_BASE_PATH;
}

export function readFinanceInvoiceListTabsFromSearch(search) {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(search || "");

  return {
    moduleTab: readInvoiceListModuleTabFromParams(params) || "application",
    statusTab: readInvoiceListStatusTabFromParams(params),
  };
}

export function buildFinanceInvoicesSearchParams({
  statusTab = "draft",
} = {}) {
  const params = new URLSearchParams();
  params.set("tab", invoiceListStatusTabSlug(statusTab));
  return params;
}

export function buildFinanceInvoicesReturnUrl({
  statusTab = "draft",
  moduleTab = "application",
} = {}) {
  const query = buildFinanceInvoicesSearchParams({
    statusTab,
  }).toString();
  return `${buildFinanceInvoicesPath(moduleTab)}?${query}`;
}

function hasInvoiceListTabQuery(params) {
  if (
    params.has("tab") ||
    params.has("status") ||
    params.has("statusTab") ||
    params.has("module") ||
    params.has("moduleTab") ||
    params.get("from") === "invoices"
  ) {
    return true;
  }

  return [...FINANCE_INVOICE_LIST_STATUS_TABS].some((id) => {
    const slug = FINANCE_INVOICE_LIST_STATUS_SLUGS[id] || id;
    return params.has(id) || params.has(slug);
  });
}

function normalizeFinanceInvoicesPathname(pathname = "") {
  return String(pathname || "").replace(/\/+$/, "") || "/";
}

export function isFinanceInvoicesHubReturnPath(value) {
  const raw = String(value || "").trim();
  if (!raw) return false;
  try {
    const url = new URL(raw, window.location.origin);
    const path = normalizeFinanceInvoicesPathname(url.pathname);
    return (
      path === FINANCE_INVOICES_BASE_PATH ||
      path === FINANCE_INVOICES_COMMISSION_PATH ||
      path === FINANCE_INVOICES_APPLICATION_PATH
    );
  } catch {
    return (
      raw === FINANCE_INVOICES_BASE_PATH ||
      raw.startsWith(`${FINANCE_INVOICES_BASE_PATH}?`) ||
      raw.startsWith(`${FINANCE_INVOICES_COMMISSION_PATH}`) ||
      raw.startsWith(`${FINANCE_INVOICES_APPLICATION_PATH}`)
    );
  }
}

/** Tabs to restore when leaving finance/commission invoice edit pages. */
export function readFinanceInvoiceListTabsFromLocation(location) {
  const params = new URLSearchParams(location?.search || "");
  const state = location?.state || {};
  const fromSearch = readFinanceInvoiceListTabsFromSearch(params);
  const path = normalizeFinanceInvoicesPathname(location?.pathname);
  const fromPath = readFinanceInvoiceModuleFromPathname(location?.pathname);
  const explicitPathModule =
    path === FINANCE_INVOICES_COMMISSION_PATH ||
    path === FINANCE_INVOICES_APPLICATION_PATH
      ? fromPath
      : null;
  const statusFromState = normalizeInvoiceListStatusTab(
    state.statusTab || state.tab,
  );
  const moduleFromState =
    state.moduleTab === "commission" || state.module === "commission"
      ? "commission"
      : state.moduleTab === "application" || state.module === "application"
        ? "application"
        : null;
  const moduleFromQuery = readInvoiceListModuleTabFromParams(params);
  const moduleFromHubFlag =
    params.get("from") === "invoices" ? "commission" : null;

  return {
    moduleTab:
      explicitPathModule ||
      moduleFromQuery ||
      fromPath ||
      moduleFromState ||
      moduleFromHubFlag ||
      fromSearch.moduleTab,
    statusTab:
      hasInvoiceListTabQuery(params)
        ? fromSearch.statusTab
        : statusFromState || fromSearch.statusTab,
  };
}

export function buildFinanceEditInvoicePath(clientId, invoiceId, tabs = {}) {
  const id = String(clientId || "").trim();
  const invoice = String(invoiceId || "").trim();
  if (!id || !invoice) return FINANCE_INVOICES_BASE_PATH;

  const query = buildFinanceInvoicesSearchParams(tabs).toString();
  return `/finance/edit-invoice/${id}/${invoice}?${query}`;
}

export function buildFinanceCreateInvoicePath(tabs = {}) {
  const query = buildFinanceInvoicesSearchParams(tabs).toString();
  return `/finance/create-invoice?${query}`;
}

export function buildCommissionHubEditInvoicePath(clientId, invoiceId, tabs = {}) {
  const id = String(clientId || "").trim();
  const invoice = String(invoiceId || "").trim();
  if (!id || !invoice) return FINANCE_INVOICES_COMMISSION_PATH;

  const params = buildFinanceInvoicesSearchParams(tabs);
  params.set("from", "invoices");
  return `/commissions/edit-invoice/${id}/${invoice}?${params.toString()}`;
}

/** POST /finance-invoices/{invoiceId}/expected-payment */
export async function updateFinanceInvoiceExpectedPayment(
  invoiceId,
  { expected_payment_date, expected_payment_note } = {},
) {
  if (!invoiceId) throw new Error("Invoice id is required.");

  try {
    const response = await apiUrl.post(
      `/finance-invoices/${invoiceId}/expected-payment`,
      {
        expected_payment_date,
        expected_payment_note,
      },
    );

    if (!isFinanceInvoiceApiSuccess(response)) {
      throw new Error(
        getFinanceInvoiceApiMessage(
          response,
          "Failed to save expected payment details.",
        ),
      );
    }

    const data = response?.data?.data ?? response?.data ?? {};
    return mapFinanceExpectedPaymentFields(data);
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to save expected payment details.",
    );
  }
}
